import React, { useMemo } from "react";
import { ChevronDown } from "lucide-react";

function cx(...a) {
  return a.filter(Boolean).join(" ");
}

function getPages(current, total) {
  // UI: 1 2 3 4 5 ... last
  // ถ้าหน้าน้อย แสดงทั้งหมด
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages = [];
  const last = total;

  // ดัน current ให้อยู่ในช่วง 1..total
  const c = Math.max(1, Math.min(current, total));

  // แสดง 1..5 แล้ว ... last (ตาม UI ตัวอย่าง)
  // แต่ถ้า current เกิน 5 ให้ขยับช่วงหน้าเล็กน้อย
  const start = c <= 4 ? 1 : Math.min(c - 2, last - 5);
  const windowPages = Array.from({ length: 5 }, (_, i) => start + i).filter((p) => p >= 1 && p <= last);

  // ทำให้มีหน้า 1 เสมอ
  if (!windowPages.includes(1)) pages.push(1);

  // ถ้าห่างมาก ใส่ ...
  const firstInWindow = windowPages[0];
  if (pages.length && firstInWindow > 2) pages.push("…");
  else if (pages.length && firstInWindow === 2) pages.push(2);

  // ใส่ window
  windowPages.forEach((p) => {
    if (!pages.includes(p)) pages.push(p);
  });

  // ถ้าห่างจาก last ใส่ ...
  const lastInWindow = windowPages[windowPages.length - 1];
  if (lastInWindow < last - 1) pages.push("…");
  if (!pages.includes(last)) pages.push(last);

  return pages;
}

export default function PaginationFooter({
  totalItems,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [50, 100, 200, 500],
}) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const safePage = Math.max(1, Math.min(page, totalPages));

  const start = totalItems === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const end = Math.min(safePage * pageSize, totalItems);

  const pages = useMemo(() => getPages(safePage, totalPages), [safePage, totalPages]);

  return (
    <div className="mt-2 flex flex-col gap-3 border-t border-slate-200 pt-4 md:flex-row md:items-center md:justify-between">
      {/* left */}
      <div className="text-sm text-[#1D1616B2]">
        แสดงรายการ {start} - {end} จาก {totalItems}
      </div>

      {/* center */}
      <div className="flex items-center gap-2">
        {pages.map((p, idx) => {
          const isActive = p === safePage;
          const isDots = p === "…";
          return (
            <button
              key={`${p}-${idx}`}
              disabled={isDots}
              onClick={() => !isDots && onPageChange?.(p)}
              className={cx(
                "h-10 w-10 rounded-xl text-sm font-semibold transition",
                isDots
                  ? "border border-slate-200 bg-white text-slate-500"
                  : isActive
                    ? "bg-blue-600 text-white shadow-sm"
                    : "border border-slate-200 bg-white text-slate-700 hover:shadow-sm active:scale-[0.98]"
              )}
            >
              {p}
            </button>
          );
        })}
      </div>

      {/* right */}
      <div className="flex items-center justify-end gap-3 text-sm text-[#1D1616B2]">
        <div className="font-medium">รายการต่อหน้า</div>

        <div className="relative">
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
            className="h-10 min-w-[84px] appearance-none rounded-xl border border-slate-200 bg-white px-3 pr-9 font-semibold text-slate-800 outline-none hover:shadow-sm"
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" />
        </div>
      </div>
    </div>
  );
}
